/**
 * Real Vercel Analytics Service
 * Fetches actual performance and analytics data from Vercel API
 */

const VERCEL_API_BASE = 'https://api.vercel.com';
const VERCEL_TOKEN = import.meta.env.VITE_VERCEL_TOKEN;
const PROJECT_ID = import.meta.env.VITE_VERCEL_PROJECT_ID;
const TEAM_ID = import.meta.env.VITE_VERCEL_TEAM_ID;

// Cache for Vercel data (refresh every 30 minutes for performance data)
const vercelCache = new Map();
const CACHE_DURATION = 30 * 60 * 1000; // 30 minutes

/**
 * Helper function to make requests to Vercel API via Supabase Edge Function
 */
const vercelRequest = async (endpoint, timeframe = '7d') => {
  const { supabase } = await import('./supabase');
  
  console.log(`Making Vercel API request via edge function: ${endpoint}`);

  const { data, error } = await supabase.functions.invoke('vercel-analytics', {
    body: { endpoint, timeframe }
  });

  if (error) {
    console.error('Vercel edge function error:', error);
    throw new Error(`Vercel edge function error: ${error.message}`);
  }

  // If we have real data (even if some endpoints return fallback), use it
  if (data && !data.error) {
    // Log if we're using fallback for specific endpoints (like analytics on hobby plan)
    if (data.fallback) {
      console.info(`Using available data for ${endpoint} (some features may be limited on current plan)`);
    }
    return data;
  }

  // Only throw if there's a real error (not just fallback data)
  if (data?.error && !data?.fallback) {
    console.error('Vercel API error:', data.error);
    throw new Error(`Vercel API error: ${data.error}`);
  }

  return data;
};

/**
 * Get cached data or fetch new data
 */
const getCachedVercelData = async (key, fetchFunction, customCacheDuration = CACHE_DURATION) => {
  const cached = vercelCache.get(key);
  const now = Date.now();
  
  if (cached && (now - cached.timestamp) < customCacheDuration) {
    console.log(`Using cached data for ${key}`);
    return cached.data;
  }
  
  try {
    console.log(`Fetching fresh data for ${key}`);
    const data = await fetchFunction();
    vercelCache.set(key, { data, timestamp: now });
    return data;
  } catch (error) {
    console.error(`Error fetching Vercel ${key}:`, error);
    // Return cached data if available, even if expired
    if (cached) {
      console.warn(`Using expired cache for ${key}`);
      return cached.data;
    }
    throw error;
  }
};

/**
 * Get deployment performance metrics (replaces Web Analytics)
 * Note: Web Analytics data is collected by @vercel/analytics but not accessible via API
 * This provides deployment and performance insights instead
 */
export const getVercelPerformanceMetrics = async () => {
  return getCachedVercelData('performance-metrics', async () => {
    if (!PROJECT_ID) {
      throw new Error('Project ID not configured. Please add VITE_VERCEL_PROJECT_ID to your .env file');
    }

    try {
      // Get recent deployments for performance analysis
      const deployments = await vercelRequest('deployments');
      const recentDeployments = deployments.deployments.slice(0, 10);
      
      // Calculate deployment metrics
      const successfulDeployments = recentDeployments.filter(d => d.state === 'READY');
      const failedDeployments = recentDeployments.filter(d => d.state === 'ERROR');
      const buildTimes = recentDeployments
        .filter(d => d.buildingAt && d.readyAt)
        .map(d => new Date(d.readyAt) - new Date(d.buildingAt));
      
      const averageBuildTime = buildTimes.length > 0 
        ? buildTimes.reduce((a, b) => a + b, 0) / buildTimes.length 
        : 0;
      
      // Calculate deployment frequency (deployments per day over last week)
      const oneWeekAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
      const recentDeploymentsCount = recentDeployments.filter(
        d => new Date(d.createdAt) > oneWeekAgo
      ).length;
      
      return {
        totalDeployments: recentDeployments.length,
        successfulDeployments: successfulDeployments.length,
        failedDeployments: failedDeployments.length,
        successRate: recentDeployments.length > 0 
          ? (successfulDeployments.length / recentDeployments.length) * 100 
          : 100,
        averageBuildTime: Math.round(averageBuildTime / 1000), // Convert to seconds
        deploymentFrequency: recentDeploymentsCount,
        lastDeployment: recentDeployments[0] || null,
        deploymentTrend: recentDeploymentsCount >= 7 ? 'high' : recentDeploymentsCount >= 3 ? 'medium' : 'low'
      };
    } catch (error) {
      console.warn('Performance metrics not available:', error.message);
      return {
        totalDeployments: 0,
        successfulDeployments: 0,
        failedDeployments: 0,
        successRate: 100,
        averageBuildTime: 0,
        deploymentFrequency: 0,
        lastDeployment: null,
        deploymentTrend: 'low'
      };
    }
  });
};

/**
 * Get deployment and performance data
 */
export const getVercelDeployments = async () => {
  return getCachedVercelData('deployments', async () => {
    if (!PROJECT_ID) {
      throw new Error('Project ID not configured');
    }

    // Get recent deployments
    const deployments = await vercelRequest('deployments');
    
    return {
      deployments: deployments.deployments.map(deployment => ({
        id: deployment.uid,
        url: deployment.url,
        state: deployment.state,
        createdAt: deployment.createdAt,
        buildTime: deployment.buildingAt && deployment.readyAt ? 
          new Date(deployment.readyAt) - new Date(deployment.buildingAt) : null,
        creator: deployment.creator?.username || 'System',
        target: deployment.target
      }))
    };
  });
};

/**
 * Get project information and settings
 */
export const getVercelProjectInfo = async () => {
  return getCachedVercelData('project-info', async () => {
    if (!PROJECT_ID) {
      throw new Error('Project ID not configured');
    }

    const project = await vercelRequest('project');
    
    return {
      name: project.name,
      framework: project.framework,
      nodeVersion: project.nodeVersion,
      buildCommand: project.buildCommand,
      outputDirectory: project.outputDirectory,
      installCommand: project.installCommand,
      devCommand: project.devCommand,
      createdAt: project.createdAt,
      updatedAt: project.updatedAt,
      live: project.targets?.production?.url || `https://${project.name}.vercel.app`
    };
  });
};

/**
 * Get bandwidth and request metrics
 */
export const getVercelUsageMetrics = async () => {
  return getCachedVercelData('usage-metrics', async () => {
    try {
      // Get usage data for the current period
      const usage = await vercelRequest('usage');
      
      return {
        bandwidth: usage.bandwidth || { used: 0, limit: 0 },
        requests: usage.requests || { used: 0, limit: 0 },
        edgeFunctionInvocations: usage.edgeFunctionInvocations || { used: 0, limit: 0 },
        period: usage.period
      };
    } catch (error) {
      console.warn('Usage metrics not available:', error.message);
      return {
        bandwidth: { used: 0, limit: 0 },
        requests: { used: 0, limit: 0 },
        edgeFunctionInvocations: { used: 0, limit: 0 }
      };
    }
  });
};

/**
 * Get comprehensive dashboard data combining all Vercel metrics
 */
export const getVercelDashboardData = async () => {
  try {
    const [performanceMetrics, deployments, projectInfo, usageMetrics] = await Promise.allSettled([
      getVercelPerformanceMetrics(),
      getVercelDeployments(),
      getVercelProjectInfo(),
      getVercelUsageMetrics()
    ]);

    return {
      performance: performanceMetrics.status === 'fulfilled' ? performanceMetrics.value : { 
        totalDeployments: 0, successfulDeployments: 0, failedDeployments: 0, 
        successRate: 100, averageBuildTime: 0, deploymentFrequency: 0, 
        lastDeployment: null, deploymentTrend: 'low'
      },
      deployments: deployments.status === 'fulfilled' ? deployments.value.deployments : [],
      project: projectInfo.status === 'fulfilled' ? projectInfo.value : { 
        name: 'Unknown', framework: 'Unknown' 
      },
      usage: usageMetrics.status === 'fulfilled' ? usageMetrics.value : { 
        bandwidth: { used: 0 }, requests: { used: 0 } 
      },
      lastUpdated: new Date().toISOString(),
      errors: [
        performanceMetrics.status === 'rejected' && `Performance: ${performanceMetrics.reason.message}`,
        deployments.status === 'rejected' && `Deployments: ${deployments.reason.message}`,
        projectInfo.status === 'rejected' && `Project: ${projectInfo.reason.message}`,
        usageMetrics.status === 'rejected' && `Usage: ${usageMetrics.reason.message}`
      ].filter(Boolean)
    };
  } catch (error) {
    console.error('Error fetching Vercel dashboard data:', error);
    throw error;
  }
};

/**
 * Check Vercel configuration status
 */
export const checkVercelConfig = () => {
  return {
    hasToken: !!VERCEL_TOKEN,
    hasProjectId: !!PROJECT_ID,
    hasTeamId: !!TEAM_ID,
    isConfigured: !!(VERCEL_TOKEN && PROJECT_ID),
    missingConfig: [
      !VERCEL_TOKEN && 'VITE_VERCEL_TOKEN',
      !PROJECT_ID && 'VITE_VERCEL_PROJECT_ID'
    ].filter(Boolean)
  };
};

/**
 * Clear Vercel cache
 */
export const clearVercelCache = () => {
  vercelCache.clear();
  console.log('Vercel cache cleared');
};

/**
 * Test Vercel API connection
 */
export const testVercelConnection = async () => {
  try {
    const config = checkVercelConfig();
    if (!config.isConfigured) {
      return {
        success: false,
        error: `Missing configuration: ${config.missingConfig.join(', ')}`,
        config
      };
    }

    console.log('Testing Vercel API connection...');
    
    // Test basic API access
    const user = await vercelRequest('user');
    
    console.log('Vercel API connection successful');
    
    return {
      success: true,
      user: {
        username: user.username,
        email: user.email,
        name: user.name
      },
      config
    };
  } catch (error) {
    console.error('Vercel API connection failed:', error);
    return {
      success: false,
      error: error.message,
      config: checkVercelConfig()
    };
  }
};

/**
 * Get formatted metrics for dashboard display
 */
export const getVercelMetricsForDashboard = async () => {
  try {
    const data = await getVercelDashboardData();
    
    console.log('Raw Vercel dashboard data:', data);
    
    // Format the data for dashboard consumption using the new performance metrics
    return {
      performance: {
        // Note: Web Analytics data is collected by @vercel/analytics but not accessible via API
        // These metrics focus on deployment and infrastructure performance instead
        totalDeployments: data.performance.totalDeployments || 0,
        successfulDeployments: data.performance.successfulDeployments || 0,
        deploymentSuccessRate: data.performance.successRate || 100,
        deploymentFrequency: data.performance.deploymentFrequency || 0,
        lastDeployment: data.performance.lastDeployment?.createdAt || data.deployments[0]?.createdAt || null,
        averageBuildTime: data.performance.averageBuildTime || 0,
        deploymentTrend: data.performance.deploymentTrend || 'low'
      },
      usage: {
        bandwidthUsed: data.usage.bandwidth?.used || 0,
        bandwidthLimit: data.usage.bandwidth?.limit || 0,
        requestsUsed: data.usage.requests?.used || 0,
        requestsLimit: data.usage.requests?.limit || 0
      },
      project: {
        name: data.project.name || 'Solis Games',
        framework: data.project.framework || 'React',
        liveUrl: data.project.live || 'https://solisgames.vercel.app'
      },
      health: {
        successfulDeployments: data.performance.successfulDeployments || 0,
        failedDeployments: data.performance.failedDeployments || 0,
        uptime: data.performance.successRate || 100
      }
    };
  } catch (error) {
    console.error('Error getting Vercel metrics for dashboard:', error);
    throw error;
  }
};
